import React, { useState, useEffect, useRef } from 'react'
import apiClient, { API_ENDPOINTS } from '../../config/api'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import html2canvas from 'html2canvas'
import Swal from 'sweetalert2'

const formatNumber = (value) => {
  const num = Number(value ?? 0)
  if (isNaN(num)) return '0.00'
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

const formatCurrency = (value) => `$${formatNumber(value)}`

const formatPercentage = (value) => {
  const num = Number(value ?? 0)
  if (isNaN(num)) return '0.00'
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

const Reports = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [returnsRate, setReturnsRate] = useState(null)

  // Chart refs
  const yearOverYearChartRef = useRef(null)
  const categoryRevenueChartRef = useRef(null)
  const topProductsChartRef = useRef(null)
  const branchRevenueChartRef = useRef(null)
  const warehouseRevenueChartRef = useRef(null)
  const stockTransferTypeChartRef = useRef(null)
  const stockTransferStatusChartRef = useRef(null)
  const stockTransferDailyChartRef = useRef(null)
  const topSourceWarehousesChartRef = useRef(null)
  const topDestinationBranchesChartRef = useRef(null)
  const reportsSectionRef = useRef(null)

  useEffect(() => {
    const fetchReports = async () => {
      setLoading(true)
      setError(null)
      try {
        const [analyticsRes, returnsRateRes] = await Promise.all([
          apiClient.get(API_ENDPOINTS.ANALYTICS_REPORTS),
          apiClient.get(API_ENDPOINTS.SALES_RETURNS_RATE).catch(err => {
            // If returns rate API fails, log but don't fail the whole report
            console.warn('Error fetching returns rate:', err)
            return { data: null }
          })
        ])
        setData(analyticsRes.data)
        setReturnsRate(returnsRateRes.data)
      } catch (err) {
        console.error('Error fetching reports:', err)
        setError('Failed to load reports. Please try again later.')
      } finally {
        setLoading(false)
      }
    }

    fetchReports()
  }, [])

  // Initialize charts when data is available
  useEffect(() => {
    if (!data || !window.ApexCharts) return

    const destroyCharts = () => {
      const refs = [
        yearOverYearChartRef, categoryRevenueChartRef, topProductsChartRef,
        branchRevenueChartRef, warehouseRevenueChartRef, stockTransferTypeChartRef,
        stockTransferStatusChartRef, stockTransferDailyChartRef,
        topSourceWarehousesChartRef, topDestinationBranchesChartRef
      ]
      refs.forEach(ref => {
        if (ref.current?.chart) {
          ref.current.chart.destroy()
          ref.current.chart = null
        }
      })
    }

    destroyCharts()

    // Year Over Year Revenue - Column Chart
    if (data.year_over_year_revenue?.length && yearOverYearChartRef.current) {
      const chart = new window.ApexCharts(yearOverYearChartRef.current, {
        series: [
          {
            name: 'Current Year',
            data: data.year_over_year_revenue.map(item => {
              const val = Number(item.current_year ?? 0)
              return isNaN(val) ? 0 : val
            })
          },
          {
            name: 'Previous Year',
            data: data.year_over_year_revenue.map(item => {
              const val = Number(item.previous_year ?? 0)
              return isNaN(val) ? 0 : val
            })
          }
        ],
        chart: {
          type: 'bar',
          height: 350,
          toolbar: { show: true }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            endingShape: 'rounded'
          }
        },
        xaxis: {
          categories: data.year_over_year_revenue.map(item => item.month_label)
        },
        yaxis: {
          labels: {
            formatter: (val) => formatCurrency(val)
          }
        },
        tooltip: {
          y: {
            formatter: (val) => formatCurrency(val)
          }
        },
        colors: ['#4154f1', '#ff771d'],
        fill: { opacity: 1 }
      })
      chart.render()
      yearOverYearChartRef.current.chart = chart
    }

    // Category Revenue Trend - Pie Chart
    if (data.category_revenue_trend?.length && categoryRevenueChartRef.current) {
      const chart = new window.ApexCharts(categoryRevenueChartRef.current, {
        series: data.category_revenue_trend.map(item => {
          const revenue = Number(item.revenue ?? 0)
          return isNaN(revenue) ? 0 : revenue
        }),
        chart: {
          type: 'pie',
          height: 350,
          toolbar: { show: true }
        },
        labels: data.category_revenue_trend.map(item => item.category_name),
        tooltip: {
          y: {
            formatter: (val) => formatCurrency(val)
          }
        },
        colors: ['#4154f1', '#2eca6a', '#ff771d', '#f72585', '#7209b7', '#3a0ca3', '#4361ee', '#4cc9f0']
      })
      chart.render()
      categoryRevenueChartRef.current.chart = chart
    }

    // Top Revenue Products - Bar Chart
    if (data.top_revenue_products?.length && topProductsChartRef.current) {
      const chart = new window.ApexCharts(topProductsChartRef.current, {
        series: [{
          name: 'Revenue',
          data: data.top_revenue_products.map(item => {
            const revenue = Number(item.revenue ?? 0)
            return isNaN(revenue) ? 0 : revenue
          })
        }],
        chart: {
          type: 'bar',
          height: 350,
          toolbar: { show: true }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4
          }
        },
        xaxis: {
          categories: data.top_revenue_products.map(item => item.product_name)
        },
        yaxis: {
          labels: {
            formatter: (val) => formatCurrency(val)
          }
        },
        tooltip: {
          y: {
            formatter: (val) => formatCurrency(val)
          }
        },
        colors: ['#4154f1']
      })
      chart.render()
      topProductsChartRef.current.chart = chart
    }

    // Branch Revenue Trend - Donut Chart
    if (data.branch_revenue_trend?.length && branchRevenueChartRef.current) {
      const chart = new window.ApexCharts(branchRevenueChartRef.current, {
        series: data.branch_revenue_trend.map(item => {
          const revenue = Number(item.revenue ?? 0)
          return isNaN(revenue) ? 0 : revenue
        }),
        chart: {
          type: 'donut',
          height: 350,
          toolbar: { show: true }
        },
        labels: data.branch_revenue_trend.map(item => item.branch_name),
        tooltip: {
          y: {
            formatter: (val) => formatCurrency(val)
          }
        },
        colors: ['#4154f1', '#2eca6a', '#ff771d', '#f72585', '#7209b7']
      })
      chart.render()
      branchRevenueChartRef.current.chart = chart
    }

    // Warehouse Revenue Trend - Donut Chart
    if (data.warehouse_revenue_trend?.length && warehouseRevenueChartRef.current) {
      const chart = new window.ApexCharts(warehouseRevenueChartRef.current, {
        series: data.warehouse_revenue_trend.map(item => {
          const revenue = Number(item.revenue ?? 0)
          return isNaN(revenue) ? 0 : revenue
        }),
        chart: {
          type: 'donut',
          height: 350,
          toolbar: { show: true }
        },
        labels: data.warehouse_revenue_trend.map(item => item.warehouse_name),
        tooltip: {
          y: {
            formatter: (val) => formatCurrency(val)
          }
        },
        colors: ['#2eca6a', '#4154f1', '#ff771d', '#f72585', '#7209b7']
      })
      chart.render()
      warehouseRevenueChartRef.current.chart = chart
    }

    // Stock Transfer Statistics - By Type (Pie Chart)
    if (data.stock_transfer_statistics?.by_type?.length && stockTransferTypeChartRef.current) {
      const chart = new window.ApexCharts(stockTransferTypeChartRef.current, {
        series: data.stock_transfer_statistics.by_type.map(item => {
          const count = Number(item.count ?? 0)
          return isNaN(count) ? 0 : count
        }),
        chart: {
          type: 'pie',
          height: 400,
          toolbar: { show: true }
        },
        labels: data.stock_transfer_statistics.by_type.map(item => item.transfer_type_display),
        tooltip: {
          y: {
            formatter: (val) => `${val} transfers`
          }
        },
        plotOptions: {
          pie: {
            expandOnClick: false,
            donut: {
              size: '0%'
            }
          }
        },
        legend: {
          position: 'bottom',
          horizontalAlign: 'center'
        },
        colors: ['#4154f1', '#2eca6a', '#ff771d']
      })
      chart.render()
      stockTransferTypeChartRef.current.chart = chart
    }

    // Stock Transfer Statistics - By Status (Donut Chart)
    if (data.stock_transfer_statistics?.by_status?.length && stockTransferStatusChartRef.current) {
      const chart = new window.ApexCharts(stockTransferStatusChartRef.current, {
        series: data.stock_transfer_statistics.by_status.map(item => {
          const count = Number(item.count ?? 0)
          return isNaN(count) ? 0 : count
        }),
        chart: {
          type: 'donut',
          height: 400,
          toolbar: { show: true }
        },
        labels: data.stock_transfer_statistics.by_status.map(item => item.status_display),
        tooltip: {
          y: {
            formatter: (val) => `${val} transfers`
          }
        },
        plotOptions: {
          pie: {
            expandOnClick: false,
            donut: {
              size: '60%'
            }
          }
        },
        legend: {
          position: 'bottom',
          horizontalAlign: 'center'
        },
        colors: ['#2eca6a', '#ffc107', '#dc3545', '#6c757d']
      })
      chart.render()
      stockTransferStatusChartRef.current.chart = chart
    }

    // Stock Transfer Daily Trend - Line Chart
    if (data.stock_transfer_statistics?.daily_trend?.length && stockTransferDailyChartRef.current) {
      const chart = new window.ApexCharts(stockTransferDailyChartRef.current, {
        series: [
          {
            name: 'Count',
            data: data.stock_transfer_statistics.daily_trend.map(item => {
              const count = Number(item.count ?? 0)
              return isNaN(count) ? 0 : count
            })
          },
          {
            name: 'Total Cost',
            data: data.stock_transfer_statistics.daily_trend.map(item => {
              const cost = Number(item.total_cost ?? 0)
              return isNaN(cost) ? 0 : cost
            })
          }
        ],
        chart: {
          type: 'line',
          height: 350,
          toolbar: { show: true }
        },
        xaxis: {
          categories: data.stock_transfer_statistics.daily_trend.map(item => {
            const date = new Date(item.day)
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          })
        },
        yaxis: [
          {
            title: { text: 'Count' },
            labels: { formatter: (val) => Math.round(val) }
          },
          {
            opposite: true,
            title: { text: 'Cost' },
            labels: {
              formatter: (val) => formatCurrency(val)
            }
          }
        ],
        tooltip: {
          y: [
            { formatter: (val) => `${Math.round(val)} transfers` },
            { formatter: (val) => formatCurrency(val) }
          ]
        },
        colors: ['#4154f1', '#2eca6a'],
        stroke: { curve: 'smooth', width: 3 }
      })
      chart.render()
      stockTransferDailyChartRef.current.chart = chart
    }

    // Top Source Warehouses - Bar Chart
    if (data.stock_transfer_statistics?.top_source_warehouses?.length && topSourceWarehousesChartRef.current) {
      const chart = new window.ApexCharts(topSourceWarehousesChartRef.current, {
        series: [{
          name: 'Transfer Count',
          data: data.stock_transfer_statistics.top_source_warehouses.map(item => {
            const count = Number(item.transfer_count ?? 0)
            return isNaN(count) ? 0 : count
          })
        }],
        chart: {
          type: 'bar',
          height: 300,
          toolbar: { show: true }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4
          }
        },
        xaxis: {
          categories: data.stock_transfer_statistics.top_source_warehouses.map(item => item.warehouse_name)
        },
        colors: ['#4154f1']
      })
      chart.render()
      topSourceWarehousesChartRef.current.chart = chart
    }

    // Top Destination Branches - Bar Chart
    if (data.stock_transfer_statistics?.top_destination_branches?.length && topDestinationBranchesChartRef.current) {
      const chart = new window.ApexCharts(topDestinationBranchesChartRef.current, {
        series: [{
          name: 'Transfer Count',
          data: data.stock_transfer_statistics.top_destination_branches.map(item => {
            const count = Number(item.transfer_count ?? 0)
            return isNaN(count) ? 0 : count
          })
        }],
        chart: {
          type: 'bar',
          height: 300,
          toolbar: { show: true }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4
          }
        },
        xaxis: {
          categories: data.stock_transfer_statistics.top_destination_branches.map(item => item.branch_name)
        },
        colors: ['#2eca6a']
      })
      chart.render()
      topDestinationBranchesChartRef.current.chart = chart
    }

    return () => {
      destroyCharts()
    }
  }, [data])

  if (loading) {
    return (
      <>
        <div className="pagetitle">
          <h1>Reports</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><a href="/">Home</a></li>
              <li className="breadcrumb-item active">Reports</li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading reports...</span>
            </div>
          </div>
        </section>
      </>
    )
  }

  if (error) {
    return (
      <>
        <div className="pagetitle">
          <h1>Reports</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><a href="/">Home</a></li>
              <li className="breadcrumb-item active">Reports</li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div className="alert alert-danger d-flex align-items-center" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </div>
        </section>
      </>
    )
  }

  const stockTransferStats = data?.stock_transfer_statistics || {}
  const cashierPerformance = data?.cashier_performance || {}

  const generatePDF = async () => {
    if (!data) {
      Swal.fire({
        icon: 'warning',
        title: 'No Data',
        text: 'No report data available to export.',
        confirmButtonColor: '#3085d6'
      })
      return
    }

    if (!reportsSectionRef.current) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Unable to capture report section.',
        confirmButtonColor: '#3085d6'
      })
      return
    }

    try {
      // Show loading message
      Swal.fire({
        title: 'Generating PDF...',
        text: 'Please wait while we capture the report.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading()
        }
      })

      // Capture the reports section as canvas
      const canvas = await html2canvas(reportsSectionRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: reportsSectionRef.current.scrollWidth,
        windowHeight: reportsSectionRef.current.scrollHeight
      })

      const imgData = canvas.toDataURL('image/png')
      const imgWidth = 210 // A4 width in mm
      const pageHeight = 297 // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width
      let heightLeft = imgHeight

      const doc = new jsPDF('portrait', 'mm', 'a4')
      let position = 0

      // Add first page
      doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
      heightLeft -= pageHeight

      // Add additional pages if content is longer than one page
      while (heightLeft > 0) {
        position = heightLeft - imgHeight
        doc.addPage()
        doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
        heightLeft -= pageHeight
      }

      // Save PDF
      const fileName = `Analytics_Reports_${new Date().toISOString().split('T')[0]}.pdf`
      doc.save(fileName)

      Swal.fire({
        icon: 'success',
        title: 'PDF Generated',
        text: 'The report has been downloaded successfully.',
        confirmButtonColor: '#3085d6',
        timer: 2000
      })
    } catch (error) {
      console.error('Error generating PDF:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to generate PDF. Please try again.',
        confirmButtonColor: '#3085d6'
      })
    }
  }

  return (
    <>
      <div className="pagetitle">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1>Analytics Reports</h1>
            <nav>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><a href="/">Home</a></li>
                <li className="breadcrumb-item active">Reports</li>
              </ol>
            </nav>
          </div>
          {data && (
            <button
              onClick={generatePDF}
              className="btn btn-primary"
              style={{ height: 'fit-content' }}
            >
              <i className="bi bi-file-earmark-pdf me-2"></i>
              Download PDF
            </button>
          )}
        </div>
      </div>

      <section className="section" ref={reportsSectionRef}>
        {/* Revenue Trends Section */}
        <div className="row">
          <div className="col-12">
            <h5 className="card-title mb-3">
              <i className="bi bi-graph-up-arrow text-primary me-2"></i>
              Revenue Trends
            </h5>
          </div>
        </div>

        <div className="row g-4 mb-4">
          <div className="col-lg-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body d-flex flex-column">
                <h5 className="card-title">Daily Revenue Trend</h5>
                {data?.daily_revenue_trend?.length ? (
                  <div className="table-responsive flex-grow-1">
                    <table className="table table-sm table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Date</th>
                          <th className="text-end">Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.daily_revenue_trend.map((item, idx) => {
                          const date = new Date(item.day)
                          return (
                            <tr key={idx}>
                              <td>{date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</td>
                              <td className="text-end fw-semibold">{formatCurrency(item.revenue)}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                      <tfoot className="table-light">
                        <tr>
                          <td className="fw-bold">Total</td>
                          <td className="text-end fw-bold">
                            {formatCurrency(data.daily_revenue_trend.reduce((sum, item) => sum + (item.revenue || 0), 0))}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                    <div>
                      <i className="bi bi-calendar-x fs-3 mb-2 d-block"></i>
                      <span>No daily revenue data available</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body d-flex flex-column">
                <h5 className="card-title">Weekly Revenue Trend</h5>
                {data?.weekly_revenue_trend?.length ? (
                  <div className="table-responsive flex-grow-1">
                    <table className="table table-sm table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Week</th>
                          <th className="text-end">Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.weekly_revenue_trend.map((item, idx) => {
                          const date = new Date(item.week)
                          return (
                            <tr key={idx}>
                              <td>Week of {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                              <td className="text-end fw-semibold">{formatCurrency(item.revenue)}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                      <tfoot className="table-light">
                        <tr>
                          <td className="fw-bold">Total</td>
                          <td className="text-end fw-bold">
                            {formatCurrency(data.weekly_revenue_trend.reduce((sum, item) => sum + (item.revenue || 0), 0))}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                    <div>
                      <i className="bi bi-calendar-week fs-3 mb-2 d-block"></i>
                      <span>No weekly revenue data available</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body d-flex flex-column">
                <h5 className="card-title">Monthly Revenue Trend</h5>
                {data?.monthly_revenue_trend?.length ? (
                  <div className="table-responsive flex-grow-1">
                    <table className="table table-sm table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Month</th>
                          <th className="text-end">Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.monthly_revenue_trend.map((item, idx) => {
                          const date = new Date(item.month)
                          return (
                            <tr key={idx}>
                              <td>{date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</td>
                              <td className="text-end fw-semibold">{formatCurrency(item.revenue)}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                      <tfoot className="table-light">
                        <tr>
                          <td className="fw-bold">Total</td>
                          <td className="text-end fw-bold">
                            {formatCurrency(data.monthly_revenue_trend.reduce((sum, item) => sum + (item.revenue || 0), 0))}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                    <div>
                      <i className="bi bi-calendar-month fs-3 mb-2 d-block"></i>
                      <span>No monthly revenue data available</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Year Over Year Revenue */}
        <div className="row g-4 mb-4">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title">Year Over Year Revenue Comparison</h5>
                {data?.year_over_year_revenue?.length ? (
                  <div ref={yearOverYearChartRef}></div>
                ) : (
                  <div className="text-center py-4 text-muted">
                    <i className="bi bi-bar-chart fs-3 mb-2 d-block"></i>
                    <span>No year-over-year data available</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Category and Product Revenue */}
        <div className="row g-4 mb-4">
          <div className="col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body d-flex flex-column">
                <h5 className="card-title">Revenue by Category</h5>
                {data?.category_revenue_trend?.length ? (
                  <div ref={categoryRevenueChartRef} className="flex-grow-1"></div>
                ) : (
                  <div className="text-center py-4 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                    <div>
                      <i className="bi bi-pie-chart fs-3 mb-2 d-block"></i>
                      <span>No category revenue data available</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body d-flex flex-column">
                <h5 className="card-title">Top Revenue Products</h5>
                {data?.top_revenue_products?.length ? (
                  <>
                    <div ref={topProductsChartRef} className="flex-grow-1"></div>
                    <div className="mt-3">
                      <h6 className="mb-2">Product Details</h6>
                      <div className="table-responsive">
                        <table className="table table-sm table-hover">
                          <thead className="table-light">
                            <tr>
                              <th>Product</th>
                              <th>SKU</th>
                              <th className="text-end">Revenue</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.top_revenue_products.map((product, idx) => (
                              <tr key={product.product_id || idx}>
                                <td>{product.product_name}</td>
                                <td><span className="badge bg-secondary">{product.product_sku}</span></td>
                                <td className="text-end fw-semibold">{formatCurrency(product.revenue)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                    <div>
                      <i className="bi bi-bar-chart fs-3 mb-2 d-block"></i>
                      <span>No product revenue data available</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Branch and Warehouse Revenue */}
        <div className="row g-4 mb-4">
          <div className="col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body d-flex flex-column">
                <h5 className="card-title">Revenue by Branch</h5>
                {data?.branch_revenue_trend?.length ? (
                  <>
                    <div ref={branchRevenueChartRef} className="flex-grow-1"></div>
                    <div className="mt-3">
                      <div className="table-responsive">
                        <table className="table table-sm table-hover">
                          <thead className="table-light">
                            <tr>
                              <th>Branch</th>
                              <th className="text-end">Revenue</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.branch_revenue_trend.map((branch, idx) => (
                              <tr key={branch.branch_id || idx}>
                                <td>{branch.branch_name}</td>
                                <td className="text-end fw-semibold">{formatCurrency(branch.revenue)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                    <div>
                      <i className="bi bi-pie-chart fs-3 mb-2 d-block"></i>
                      <span>No branch revenue data available</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body d-flex flex-column">
                <h5 className="card-title">Revenue by Warehouse</h5>
                {data?.warehouse_revenue_trend?.length ? (
                  <>
                    <div ref={warehouseRevenueChartRef} className="flex-grow-1"></div>
                    <div className="mt-3">
                      <div className="table-responsive">
                        <table className="table table-sm table-hover">
                          <thead className="table-light">
                            <tr>
                              <th>Warehouse</th>
                              <th className="text-end">Revenue</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.warehouse_revenue_trend.map((warehouse, idx) => (
                              <tr key={warehouse.warehouse_id || idx}>
                                <td>{warehouse.warehouse_name}</td>
                                <td className="text-end fw-semibold">{formatCurrency(warehouse.revenue)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                    <div>
                      <i className="bi bi-pie-chart fs-3 mb-2 d-block"></i>
                      <span>No warehouse revenue data available</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Cashier Performance */}
        {cashierPerformance.cashiers?.length > 0 && (
          <div className="row g-4 mb-4">
            <div className="col-12">
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="card-title mb-0">Cashier Performance</h5>
                    <div className="text-muted small">
                      Range: {cashierPerformance.selected_range && (
                        <>
                          {new Date(cashierPerformance.range_start).toLocaleDateString()} - {new Date(cashierPerformance.range_end).toLocaleDateString()}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Cashier</th>
                          <th>Email</th>
                          <th className="text-end">Total Sales Amount</th>
                          <th className="text-end">Transaction Count</th>
                          <th className="text-end">Avg Sale Value</th>
                          <th className="text-end">Items Sold</th>
                          <th className="text-end">Refunds</th>
                          <th className="text-end">Voids</th>
                          <th className="text-end">Refund Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cashierPerformance.cashiers.map((cashier, idx) => (
                          <tr key={cashier.cashier_id || idx}>
                            <td className="fw-semibold">{cashier.cashier_name}</td>
                            <td><small className="text-muted">{cashier.cashier_email}</small></td>
                            <td className="text-end">{formatCurrency(cashier.total_sales_amount)}</td>
                            <td className="text-end">{formatNumber(cashier.transaction_count)}</td>
                            <td className="text-end">{formatCurrency(cashier.average_sale_value)}</td>
                            <td className="text-end">{formatNumber(cashier.total_items_sold)}</td>
                            <td className="text-end">
                              <span className="badge bg-warning">{cashier.refund_count}</span>
                            </td>
                            <td className="text-end">
                              <span className="badge bg-danger">{cashier.void_count}</span>
                            </td>
                            <td className="text-end">
                              <span className={`badge ${cashier.refund_rate > 5 ? 'bg-danger' : cashier.refund_rate > 2 ? 'bg-warning' : 'bg-success'}`}>
                                {cashier.refund_rate.toFixed(2)}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Returns Rate Section */}
        {returnsRate && (
          <>
            <div className="row">
              <div className="col-12">
                <h5 className="card-title mb-3">
                  <i className="bi bi-arrow-counterclockwise text-primary me-2"></i>
                  Returns Rate Analysis
                </h5>
              </div>
            </div>

            {/* Returns Rate Summary Cards */}
            <div className="row g-3 mb-4">
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Total Sold Quantity</h6>
                    <h4 className="mb-0 text-primary">{formatNumber(returnsRate.metrics?.total_sold_quantity || 0)}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Total Sales Value</h6>
                    <h4 className="mb-0 text-success">{formatCurrency(returnsRate.metrics?.total_sales_value || 0)}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Total Returned Quantity</h6>
                    <h4 className="mb-0 text-danger">{formatNumber(returnsRate.metrics?.total_returned_quantity || 0)}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Total Refund Amount</h6>
                    <h4 className="mb-0 text-warning">{formatCurrency(returnsRate.metrics?.total_refund_amount || 0)}</h4>
                  </div>
                </div>
              </div>
            </div>

            {/* Return Rate Metrics */}
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Quantity Return Rate</h6>
                    <h4 className="mb-0 text-info">{formatPercentage(returnsRate.metrics?.quantity_return_rate || 0)}%</h4>
                    <small className="text-muted">
                      {returnsRate.metrics?.total_returned_quantity || 0} of {returnsRate.metrics?.total_sold_quantity || 0} items returned
                    </small>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Value Return Rate</h6>
                    <h4 className="mb-0 text-info">{formatPercentage(returnsRate.metrics?.value_return_rate || 0)}%</h4>
                    <small className="text-muted">
                      {formatCurrency(returnsRate.metrics?.total_refund_amount || 0)} of {formatCurrency(returnsRate.metrics?.total_sales_value || 0)} refunded
                    </small>
                  </div>
                </div>
              </div>
            </div>

            {/* Top Returned Products */}
            {returnsRate.top_returned_products?.length > 0 && (
              <div className="row g-4 mb-4">
                <div className="col-12">
                  <div className="card shadow-sm border-0">
                    <div className="card-body">
                      <h5 className="card-title">Top Returned Products</h5>
                      <div className="mt-3">
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead className="table-light">
                              <tr>
                                <th>Product</th>
                                <th>SKU</th>
                                <th className="text-end">Returned Quantity</th>
                                <th className="text-end">Refund Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {returnsRate.top_returned_products.map((product, idx) => (
                                <tr key={product.product_id || idx}>
                                  <td className="fw-semibold">{product.product_name}</td>
                                  <td><span className="badge bg-secondary">{product.product_sku}</span></td>
                                  <td className="text-end text-danger fw-semibold">{formatNumber(product.total_returned_quantity)}</td>
                                  <td className="text-end text-warning fw-semibold">{formatCurrency(product.total_refund_amount)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Filter Information */}
            {returnsRate.filters && (
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-body">
                      <h6 className="text-muted mb-2">Report Period</h6>
                      <p className="mb-0">
                        <strong>Start Date:</strong> {returnsRate.filters.start_date ? new Date(returnsRate.filters.start_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'} &nbsp;|&nbsp;
                        <strong>End Date:</strong> {returnsRate.filters.end_date ? new Date(returnsRate.filters.end_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}
                        {returnsRate.filters.branch_id && (
                          <> &nbsp;|&nbsp; <strong>Branch ID:</strong> {returnsRate.filters.branch_id}</>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Stock Transfer Statistics */}
        {stockTransferStats.summary && (
          <>
            <div className="row">
              <div className="col-12">
                <h5 className="card-title mb-3">
                  <i className="bi bi-arrow-left-right text-primary me-2"></i>
                  Stock Transfer Statistics
                </h5>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="row g-3 mb-4">
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Total Transfers</h6>
                    <h4 className="mb-0 text-primary">{stockTransferStats.summary.total_transfers || 0}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Completed</h6>
                    <h4 className="mb-0 text-success">{stockTransferStats.summary.completed_transfers || 0}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Pending</h6>
                    <h4 className="mb-0 text-warning">{stockTransferStats.summary.pending_transfers || 0}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Completion Rate</h6>
                    <h4 className="mb-0 text-info">{stockTransferStats.summary.completion_rate?.toFixed(1) || 0}%</h4>
                  </div>
                </div>
              </div>
            </div>

            {/* Stock Transfer Charts */}
            <div className="row g-4 mb-4">
              <div className="col-lg-4">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body d-flex flex-column">
                    <h5 className="card-title">By Type</h5>
                    {stockTransferStats.by_type?.length ? (
                      <div ref={stockTransferTypeChartRef} className="flex-grow-1"></div>
                    ) : (
                      <div className="text-center py-4 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                        <div>
                          <i className="bi bi-pie-chart fs-3 mb-2 d-block"></i>
                          <span>No data available</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-4">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body d-flex flex-column">
                    <h5 className="card-title">By Status</h5>
                    {stockTransferStats.by_status?.length ? (
                      <div ref={stockTransferStatusChartRef} className="flex-grow-1"></div>
                    ) : (
                      <div className="text-center py-4 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                        <div>
                          <i className="bi bi-pie-chart fs-3 mb-2 d-block"></i>
                          <span>No data available</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-4">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body d-flex flex-column">
                    <h5 className="card-title">Summary</h5>
                    <div className="mt-3 flex-grow-1 d-flex flex-column justify-content-center">
                      <div className="d-flex justify-content-between mb-2">
                        <span>Total Cost:</span>
                        <strong>{formatCurrency(stockTransferStats.summary.total_cost || 0)}</strong>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Total Quantity:</span>
                        <strong>{formatNumber(stockTransferStats.summary.total_quantity || 0)}</strong>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>In Transit:</span>
                        <strong>{stockTransferStats.summary.in_transit_transfers || 0}</strong>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span>Cancelled:</span>
                        <strong>{stockTransferStats.summary.cancelled_transfers || 0}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stock Transfer Daily Trend */}
            {stockTransferStats.daily_trend?.length > 0 && (
              <div className="row g-4 mb-4">
                <div className="col-12">
                  <div className="card shadow-sm border-0">
                    <div className="card-body">
                      <h5 className="card-title">Daily Transfer Trend</h5>
                      <div ref={stockTransferDailyChartRef}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Top Source Warehouses and Destination Branches */}
            <div className="row g-4 mb-4">
              {stockTransferStats.top_source_warehouses?.length > 0 && (
                <div className="col-lg-6">
                  <div className="card shadow-sm border-0 h-100">
                    <div className="card-body d-flex flex-column">
                      <h5 className="card-title">Top Source Warehouses</h5>
                      <div ref={topSourceWarehousesChartRef} className="flex-grow-1"></div>
                      <div className="mt-3">
                        <div className="table-responsive">
                          <table className="table table-sm table-hover">
                            <thead className="table-light">
                              <tr>
                                <th>Warehouse</th>
                                <th className="text-end">Transfers</th>
                                <th className="text-end">Total Cost</th>
                                <th className="text-end">Quantity</th>
                              </tr>
                            </thead>
                            <tbody>
                              {stockTransferStats.top_source_warehouses.map((warehouse, idx) => (
                                <tr key={warehouse.warehouse_id || idx}>
                                  <td>{warehouse.warehouse_name}</td>
                                  <td className="text-end">{warehouse.transfer_count}</td>
                                  <td className="text-end">{formatCurrency(warehouse.total_cost)}</td>
                                  <td className="text-end">{formatNumber(warehouse.total_quantity)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {stockTransferStats.top_destination_branches?.length > 0 && (
                <div className="col-lg-6">
                  <div className="card shadow-sm border-0 h-100">
                    <div className="card-body d-flex flex-column">
                      <h5 className="card-title">Top Destination Branches</h5>
                      <div ref={topDestinationBranchesChartRef} className="flex-grow-1"></div>
                      <div className="mt-3">
                        <div className="table-responsive">
                          <table className="table table-sm table-hover">
                            <thead className="table-light">
                              <tr>
                                <th>Branch</th>
                                <th className="text-end">Transfers</th>
                                <th className="text-end">Total Cost</th>
                                <th className="text-end">Quantity</th>
                              </tr>
                            </thead>
                            <tbody>
                              {stockTransferStats.top_destination_branches.map((branch, idx) => (
                                <tr key={branch.branch_id || idx}>
                                  <td>{branch.branch_name}</td>
                                  <td className="text-end">{branch.transfer_count}</td>
                                  <td className="text-end">{formatCurrency(branch.total_cost)}</td>
                                  <td className="text-end">{formatNumber(branch.total_quantity)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </section>
    </>
  )
}

export default Reports
